// =============================================================================
// alerts.js – Transport alerts (strikes & disruptions) display
// =============================================================================
// Fetches alerts.json (pushed by the private scraper repo) and provides:
//   - getStrikeAlerts() → only strikes (shown as banners in LIVE tab)
//   - getAllAlerts() → all alerts (shown in the dedicated Avvisi tab)
//   - renderAlertBanner() → HTML for the LIVE tab strike banner
//   - renderAlertsTab() → full HTML for the Avvisi tab
// =============================================================================

import { escapeHtml } from "./utils.js";
import { patchDOM } from "./dom-utils.js";
import { CFG } from "../data/config.js";

const ALERTS_URL = "./alerts.json";
const DISMISSED_KEY = "trasporti_dismissed_alerts";
const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

let _alertsData = null;
let _lastFetch = 0;
let _onAlertsReady = null;

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Initialize alerts: fetch on startup, set up periodic refresh.
 * @param {Function} [onReady] - called after first successful fetch (to trigger re-render)
 */
export function initAlerts(onReady) {
  _onAlertsReady = onReady || null;
  fetchAlerts();
  setInterval(fetchAlerts, REFRESH_INTERVAL);
}

/**
 * Get only strike alerts (type === "strike") that are currently active.
 * These are shown as banners in the LIVE tab.
 */
export function getStrikeAlerts() {
  if (!_alertsData?.alerts) return [];
  const now = Date.now();
  const dismissedMap = getDismissedMap();
  return _alertsData.alerts.filter((a) => {
    if (a.type !== "strike") return false;
    if (!isActiveNow(a, now)) return false;

    const dismissedAt = dismissedMap[a.id];
    if (dismissedAt) {
      if (a.startDate) {
        const start = new Date(a.startDate).getTime();
        const reminderStartTime = start - 24 * 60 * 60 * 1000;

        // Se è stato cancellato PRIMA che iniziasse la finestra di 24 ore,
        // ma ora siamo nelle ultime 24 ore, ignoriamo la cancellazione precedente
        // e lo visualizziamo di nuovo come "reminder".
        if (dismissedAt < reminderStartTime && now >= reminderStartTime) {
          return true;
        }
      }
      return false;
    }
    return true;
  });
}

/**
 * Get all alerts grouped by category for the Avvisi tab.
 * Returns { strikes: [...], notices: [...], lastUpdated: string }
 */
export function getAllAlerts() {
  if (!_alertsData?.alerts) return { strikes: [], notices: [], lastUpdated: null };
  const now = Date.now();
  const active = _alertsData.alerts.filter((a) => isActiveOrUpcoming(a, now));
  return {
    strikes: active.filter((a) => a.type === "strike"),
    notices: active.filter((a) => a.type !== "strike"),
    lastUpdated: _alertsData.lastUpdated || null
  };
}

/**
 * Checks if an alert affects a list of followed lines.
 */
function isAlertAffectingFollowed(alert, followedLines) {
  return followedLines.some((lineId) => {
    const services = alert.affectedServices || [];
    if (services.includes(lineId)) return true;
    const replacements = alert.busReplacements || [];
    if (replacements.some((r) => r.line === lineId)) return true;
    return false;
  });
}

/**
 * Get active alerts affecting a specific line.
 */
export function getAlertsForLine(lineId) {
  if (!_alertsData?.alerts) return [];
  const now = Date.now();
  return _alertsData.alerts.filter((a) => {
    if (!isActiveOrUpcoming(a, now)) return false;
    const services = a.affectedServices || [];
    if (services.includes(lineId)) return true;
    const replacements = a.busReplacements || [];
    if (replacements.some((r) => r.line === lineId)) return true;
    return false;
  });
}

/**
 * Dismiss an alert (hide from LIVE banner).
 */
export function dismissAlert(alertId) {
  const dismissed = getDismissedMap();
  dismissed[alertId] = Date.now();
  localStorage.setItem(DISMISSED_KEY + "_v2", JSON.stringify(dismissed));
}

/**
 * Generates visual HTML chips/labels for strike guarantee bands, strike windows, and bus replacements.
 */
function getStrikeChipsHtml(alert) {
  let html = "";

  // 1. Bus Replacements
  const bReplacements = alert.busReplacements || [];
  if (Array.isArray(bReplacements) && bReplacements.length > 0) {
    const chips = bReplacements
      .map((r) => {
        const lineStr = r.line ? `${r.line}: ` : "";
        const label = `🚌 ${lineStr}${r.from} ➔ ${r.to}`;
        return `<span class="band-chip band-chip--bus" title="Bus sostitutivo">${escapeHtml(label)}</span>`;
      })
      .join("");
    html += `<div class="alert-card-bands"><span class="alert-card-bands-label">Bus Sostitutivi:</span>${chips}</div>`;
  }

  // 2. Guaranteed Bands
  const gBands = alert.guaranteedBands || [];
  if (Array.isArray(gBands) && gBands.length > 0) {
    const chips = gBands
      .map((b) => {
        let svcName = b.service || "";
        if (svcName === "Funicolare Como-Brunate") {
          svcName = "Funicolare";
        }
        let label = svcName ? `${svcName} ` : "";

        let dateStr = "";
        if (b.date && alert.startDate) {
          try {
            const mainStart = new Date(alert.startDate);
            const bandD = new Date(b.date);
            const sameDay =
              mainStart.getFullYear() === bandD.getFullYear() &&
              mainStart.getMonth() === bandD.getMonth() &&
              mainStart.getDate() === bandD.getDate();
            if (!sameDay) {
              dateStr = bandD.toLocaleDateString("it-IT", { day: "numeric", month: "short" }) + " ";
            }
          } catch (_e) {
            // Ignore invalid dates
          }
        }
        label += `${dateStr}${b.from}-${b.to}`;
        return `<span class="band-chip band-chip--guaranteed" title="Fascia garantita">🛡️ ${escapeHtml(label)}</span>`;
      })
      .join("");
    html += `<div class="alert-card-bands"><span class="alert-card-bands-label">Garantite:</span>${chips}</div>`;
  }

  // 3. Strike Windows
  const sWindows = alert.strikeWindows || [];
  if (Array.isArray(sWindows) && sWindows.length > 0) {
    const chips = sWindows
      .map((w) => {
        let svcName = w.service || "";
        if (svcName === "Funicolare Como-Brunate") {
          svcName = "Funicolare";
        }
        let label = svcName ? `${svcName} ` : "";

        let dateStr = "";
        if (w.date && alert.startDate) {
          try {
            const mainStart = new Date(alert.startDate);
            const bandD = new Date(w.date);
            const sameDay =
              mainStart.getFullYear() === bandD.getFullYear() &&
              mainStart.getMonth() === bandD.getMonth() &&
              mainStart.getDate() === bandD.getDate();
            if (!sameDay) {
              dateStr = bandD.toLocaleDateString("it-IT", { day: "numeric", month: "short" }) + " ";
            }
          } catch (_e) {
            // Ignore invalid dates
          }
        }

        const toVal = w.to === "fine_servizio" ? "→ fine serv." : w.to;
        const separator = w.to === "fine_servizio" ? " " : "-";
        label += `${dateStr}${w.from}${separator}${toVal}`;
        return `<span class="band-chip band-chip--strike" title="Finestra di sciopero">🚫 ${escapeHtml(label)}</span>`;
      })
      .join("");
    html += `<div class="alert-card-bands"><span class="alert-card-bands-label">Non garantite:</span>${chips}</div>`;
  }

  // Fallback for legacy scraper output (guaranteedBands as string)
  if (html === "" && alert.guaranteedBands && typeof alert.guaranteedBands === "string") {
    const chips = alert.guaranteedBands
      .split(",")
      .map((b) => b.trim())
      .filter(Boolean)
      .map((b) => `<span class="band-chip">${escapeHtml(b)}</span>`)
      .join("");
    html = `<div class="alert-card-bands"><span class="alert-card-bands-label">Fasce di garanzia:</span>${chips}</div>`;
  }

  return html;
}

/**
 * Render the strike banner for the LIVE tab.
 * Returns HTML string (empty if no active strikes).
 */
export function renderStrikeBanner() {
  const strikes = getStrikeAlerts();
  if (strikes.length === 0) return "";

  const now = Date.now();

  return strikes
    .map((alert) => {
      // Deduplicate: remove specific line codes from affectedServices list if already shown in busReplacements
      let servicesList = alert.affectedServices || [];
      const busLines = new Set((alert.busReplacements || []).map((r) => r.line).filter(Boolean));
      servicesList = servicesList.filter((s) => !busLines.has(s));
      const services = servicesList.join(", ");

      const dateRange = formatStrikeDateRange(alert.startDate, alert.endDate);
      const sourceLabel = { trenord: "Trenord", atm: "ATM Milano" }[alert.source] || alert.source;
      const typeIcon = "🚨";

      // Determine if we should display the reminder tag
      let reminderTag = "";
      if (alert.startDate) {
        const start = new Date(alert.startDate).getTime();
        const diffMs = start - now;
        if (diffMs > 0 && diffMs <= 24 * 60 * 60 * 1000) {
          reminderTag = `<span class="alert-strike-reminder-tag">reminder</span> `;
        }
      }

      const countdownBadge = getCountdownBadge(alert.startDate, alert.endDate);
      const chipsHtml = getStrikeChipsHtml(alert);
      const descToDisplay = alert.summary || alert.description;

      return `<div class="alert-card alert-card--strike alert-strike-banner" data-alert-id="${escapeHtml(alert.id)}">
      <button type="button" class="alert-card-dismiss alert-strike-dismiss" data-dismiss-alert="${escapeHtml(alert.id)}" aria-label="Chiudi avviso: ${escapeHtml(alert.title)}">✕</button>
      <div class="alert-card-header">
        <span class="alert-card-icon">${typeIcon}</span>
        <div class="alert-card-meta">
          <span class="alert-card-source">${escapeHtml(sourceLabel)}</span>
          ${countdownBadge}
        </div>
      </div>
      <h3 class="alert-card-title">${reminderTag}${escapeHtml(alert.title)}</h3>
      ${dateRange ? `<div class="alert-card-daterange">${dateRange}</div>` : ""}
      ${descToDisplay && descToDisplay !== alert.title ? `<p class="alert-card-desc">${escapeHtml(descToDisplay.slice(0, 300))}</p>` : ""}
      <div class="alert-card-footer">
        ${services ? `<span class="alert-card-services">${escapeHtml(services)}</span>` : ""}
        ${chipsHtml}
      </div>
      ${alert.url ? `<a href="${escapeHtml(alert.url)}" target="_blank" rel="noopener" class="alert-card-link">Dettagli →</a>` : ""}
    </div>`;
    })
    .join("");
}

/**
 * Render the full Avvisi tab content.
 */
export function renderAlertsTab(state) {
  const container = document.getElementById("alerts-content");
  if (!container) return;

  let { strikes, notices, lastUpdated } = getAllAlerts();
  const updatedStr = lastUpdated ? formatAlertDate(lastUpdated) : "mai";

  if (state && state.onlyMyLinesAlerts) {
    const focusCity = state.settings?.focusCity || CFG.defaults?.focusCity || "BT";
    const cityLineOrder = CFG.focusCities?.[focusCity]?.lineOrder || CFG.lineOrder || [];
    const followedForCity = state.settings?.followedLinesByCity?.[focusCity];
    const followedLines = Array.isArray(followedForCity) ? followedForCity : cityLineOrder;

    strikes = strikes.filter((a) => isAlertAffectingFollowed(a, followedLines));
    notices = notices.filter((a) => isAlertAffectingFollowed(a, followedLines));
  }

  const checkboxChecked = state && state.onlyMyLinesAlerts ? "checked" : "";

  let html = `
    <div class="page-head" style="--lc: var(--accent);">
      <h1>Avvisi</h1>
      <p>Disservizi e modifiche al servizio</p>
    </div>
    <div class="alerts-toolbar">
      <input type="checkbox" id="alerts-only-my-lines" ${checkboxChecked}>
      <label for="alerts-only-my-lines">Solo le mie linee</label>
      <span style="margin-left:auto; font-size: 10px; color: var(--text-dim, var(--quiet));">Ultimo agg.: ${escapeHtml(updatedStr)}</span>
    </div>
  `;

  // P9: bucket by time — "Oggi" (currently active or <=24h) vs "Programmati" (later).
  const now = Date.now();
  const todayMidnight = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
  const bucketToday = [];
  const bucketUpcoming = [];
  [...strikes, ...notices].forEach((a) => {
    if (!a.startDate) { bucketToday.push({ a, cat: a.type === "strike" ? "strike" : "notice" }); return; }
    const start = new Date(a.startDate).getTime();
    const end = a.endDate ? new Date(a.endDate).getTime() : start + 86400000;
    const isToday = (now >= start - 86400000 && now <= end) || start <= todayMidnight + 86400000;
    const cat = a.type === "strike" ? "strike" : "notice";
    if (isToday) bucketToday.push({ a, cat });
    else bucketUpcoming.push({ a, cat });
  });
  // Sort each bucket by start-date ascending.
  const sortByStart = (x, y) => (new Date(x.a.startDate ?? 0).getTime() || 0) - (new Date(y.a.startDate ?? 0).getTime() || 0);
  bucketToday.sort(sortByStart);
  bucketUpcoming.sort(sortByStart);

  if (bucketToday.length > 0) {
    html += `<div class="section-head" style="margin: 0 20px;"><span class="label">Oggi</span></div>`;
    html += bucketToday.map(({ a, cat }) => renderAlertCard(a, cat)).join("");
  }
  if (bucketUpcoming.length > 0) {
    html += `<div class="section-head" style="margin: 16px 20px 0;"><span class="label">Programmati</span></div>`;
    html += bucketUpcoming.map(({ a, cat }) => renderAlertCard(a, cat)).join("");
  }

  // Empty state
  if (bucketToday.length === 0 && bucketUpcoming.length === 0) {
    html += `<div class="empty-state">
      <strong>Nessun avviso attivo</strong><br>
      <small>Nessuno sciopero o disservizio segnalato per i prossimi giorni. Buon viaggio! 🚌</small>
    </div>`;
  }

  patchDOM(container, html);

  const filterCheckbox = container.querySelector("#alerts-only-my-lines");
  if (filterCheckbox) {
    filterCheckbox.addEventListener("change", () => {
      if (state) {
        state.onlyMyLinesAlerts = filterCheckbox.checked;
        renderAlertsTab(state);
      }
    });
  }
}

// ── Internal ────────────────────────────────────────────────────────────────

/**
 * Generate a dynamic countdown badge HTML string.
 * Shows "🔴 In corso" if the strike is active now, or "fra N giorni" / "domani" if upcoming.
 */
function getCountdownBadge(startIso, endIso) {
  if (!startIso) return "";
  const now = Date.now();
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : start + 24 * 60 * 60 * 1000; // default to 24h duration

  if (now >= start && now <= end) {
    return `<span class="alert-countdown-badge alert-countdown-badge--active">🔴 In corso</span>`;
  }

  if (now < start) {
    const diffMs = start - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 1) {
      return `<span class="alert-countdown-badge alert-countdown-badge--upcoming">domani</span>`;
    }
    return `<span class="alert-countdown-badge alert-countdown-badge--upcoming">fra ${diffDays} giorni</span>`;
  }

  return "";
}

function renderAlertCard(alert, category) {
  // Deduplicate: remove specific line codes from affectedServices list if already shown in busReplacements
  const alertStart = alert.startDate ? new Date(alert.startDate).getTime() : NaN;
  const alertEnd = alert.endDate ? new Date(alert.endDate).getTime() : (!Number.isNaN(alertStart) ? alertStart + 86400000 : NaN);
  const now = Date.now();
  const isToday = !Number.isNaN(alertStart) && now >= (alertStart - 86400000) && now <= alertEnd;

  // P9: Figma severity model:
  //   sev-high  — active strikes (currently running) — danger gradient
  //   sev-mid   — non-strike warnings with strong impact or imminent (≤48h) strikes
  //   plain     — upcoming strikes / scheduled infos
  let sev = "";
  if (category === "strike" && isToday) sev = "sev-high";
  else if (category === "strike" && !Number.isNaN(alertStart) && (alertStart - now) <= 2 * 86400000) sev = "sev-mid";

  // Tag label/modifier
  let tagLabel, tagModifier;
  if (sev === "sev-high") { tagLabel = "CRITICO"; tagModifier = ""; }
  else if (sev === "sev-mid") { tagLabel = "AVVISO"; tagModifier = ""; }
  else if (category === "strike") { tagLabel = "AVVISO"; tagModifier = ""; }
  else { tagLabel = "INFO"; tagModifier = "info"; }

  // When label: HH:MM if today, else dd MMM; currently active shows "In corso".
  let whenText = "";
  if (alertStart && isToday) {
    if (now >= alertStart && now <= alertEnd) whenText = "In corso";
    else whenText = new Date(alert.startDate).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } else if (alertStart) {
    whenText = new Date(alert.startDate).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } else if (alert.publishedAt) {
    whenText = new Date(alert.publishedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  }

  // Affected-lines: chip per line, colored with --line-* token
  let servicesList = alert.affectedServices || [];
  const busLines = new Set((alert.busReplacements || []).map((r) => r.line).filter(Boolean));
  servicesList = servicesList.filter((s) => !busLines.has(s));
  const linesHtml = servicesList.length
    ? `<div class="affected-lines">${servicesList.map((s) => {
        const token = `var(--line-${String(s).toLowerCase()}, var(--accent))`;
        return `<span class="line-chip" style="background: ${token};">${escapeHtml(s)}</span>`;
      }).join("")}</div>`
    : "";

  const descToDisplay = alert.summary || alert.description;
  const dateRange = formatStrikeDateRange(alert.startDate, alert.endDate);

  return `<div class="alert-card ${sev}">
    <div class="head">
      <span class="tag ${tagModifier}">${tagLabel}</span>
      ${whenText ? `<span class="when">${escapeHtml(whenText)}</span>` : ""}
    </div>
    <h4>${escapeHtml(alert.title)}</h4>
    ${descToDisplay && descToDisplay !== alert.title ? `<p>${escapeHtml(descToDisplay.slice(0, 300))}</p>` : ""}
    ${dateRange ? `<p style="font-size: 11px; color: var(--text-dim, var(--quiet)); margin-top: 4px;">${dateRange}</p>` : ""}
    ${linesHtml}
    ${alert.url ? `<a href="${escapeHtml(alert.url)}" target="_blank" rel="noopener" class="alert-card-link">Dettagli →</a>` : ""}
  </div>`;
}

async function fetchAlerts() {
  try {
    const res = await fetch(ALERTS_URL, { cache: "no-cache" });
    if (!res.ok) return;
    const wasEmpty = !_alertsData;
    _alertsData = await res.json();
    _lastFetch = Date.now();
    cleanDismissed();
    // On first successful fetch, trigger a re-render so banners appear
    if (wasEmpty && _onAlertsReady && _alertsData.alerts?.length > 0) {
      _onAlertsReady();
    }
  } catch (e) {
    // Silently fail — app works without alerts
  }
}

function isActiveNow(alert, now) {
  // Uno sciopero è attivo ora se:
  // - Non è terminato (endDate è futura o non definita)
  // - Inizia entro 8 giorni
  if (alert.endDate && new Date(alert.endDate).getTime() < now) return false;
  if (alert.startDate) {
    const start = new Date(alert.startDate).getTime();

    // In corso o iniziato nelle ultime 48 ore (se non c'è data di fine)
    const twoDaysAgo = now - 2 * 24 * 60 * 60 * 1000;
    const isInProgress = start >= twoDaysAgo && start <= now;

    // Inizia entro 8 giorni
    const isWithin8Days = start > now && start <= now + 8 * 24 * 60 * 60 * 1000;

    return isInProgress || isWithin8Days;
  }
  return true;
}

function isActiveOrUpcoming(alert, now) {
  // For the Avvisi tab: show anything from the past 7 days or upcoming
  if (alert.endDate && new Date(alert.endDate).getTime() < now) return false;
  if (alert.startDate) {
    const start = new Date(alert.startDate).getTime();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    return start > sevenDaysAgo;
  }
  return true;
}

function getDismissedMap() {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY + "_v2") || "{}");
  } catch (e) {
    return {};
  }
}

function getDismissed() {
  const map = getDismissedMap();
  return Object.keys(map);
}

function cleanDismissed() {
  if (!_alertsData?.alerts) return;
  const activeIds = new Set(_alertsData.alerts.map((a) => a.id));
  const dismissed = getDismissedMap();
  let changed = false;
  for (const id of Object.keys(dismissed)) {
    if (!activeIds.has(id)) {
      delete dismissed[id];
      changed = true;
    }
  }
  if (changed) {
    localStorage.setItem(DISMISSED_KEY + "_v2", JSON.stringify(dismissed));
  }
}

function formatAlertDate(isoStr) {
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("it-IT", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch (e) {
    return "";
  }
}

/**
 * Format a clear date range string for strikes.
 * Example output: "🚨 21:00 del 17 maggio → 20:59 del 18 maggio 2026"
 * @param {string|null} startIso
 * @param {string|null} endIso
 * @returns {string} HTML-safe formatted range (may contain emoji)
 */
function formatStrikeDateRange(startIso, endIso) {
  if (!startIso && !endIso) return "";

  // Filter out dates that are local midnight (00:00) or end of day (23:59) — likely imprecise/auto-generated
  const isReliableTime = (isoStr) => {
    if (!isoStr) return false;
    const d = new Date(isoStr);
    const h = d.getHours();
    const m = d.getMinutes();
    // Midnight local (00:00) or 23:59 local is considered a date-only placeholder
    if (h === 0 && m === 0) return false;
    if (h === 23 && m === 59) return false;
    return true;
  };

  const hasReliableStart = isReliableTime(startIso);
  const hasReliableEnd = isReliableTime(endIso);

  const formatDateOnly = (isoStr, includeYear) => {
    try {
      const d = new Date(isoStr);
      const day = d.getDate();
      const month = d.toLocaleDateString("it-IT", { month: "long" });
      const year = d.getFullYear();
      return includeYear ? `${day} ${month} ${year}` : `${day} ${month}`;
    } catch (e) {
      return "";
    }
  };

  const formatDateTime = (isoStr, includeYear) => {
    try {
      const d = new Date(isoStr);
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      const day = d.getDate();
      const month = d.toLocaleDateString("it-IT", { month: "long" });
      const year = d.getFullYear();
      return includeYear
        ? `${hh}:${mm} del ${day} ${month} ${year}`
        : `${hh}:${mm} del ${day} ${month}`;
    } catch (e) {
      return "";
    }
  };

  // Case 1: Both start and end have reliable hours
  if (startIso && endIso && hasReliableStart && hasReliableEnd) {
    const startD = new Date(startIso);
    const endD = new Date(endIso);
    const sameYear = startD.getFullYear() === endD.getFullYear();
    const startStr = formatDateTime(startIso, !sameYear);
    const endStr = formatDateTime(endIso, true);
    return `⏰ ${startStr} → ${endStr}`;
  }

  // Case 2: Date-only (both times are placeholders/unreliable)
  if (startIso && endIso && !hasReliableStart && !hasReliableEnd) {
    const startD = new Date(startIso);
    const endD = new Date(endIso);
    const sameDay =
      startD.getFullYear() === endD.getFullYear() &&
      startD.getMonth() === endD.getMonth() &&
      startD.getDate() === endD.getDate();

    if (sameDay) {
      return `⏰ ${formatDateOnly(startIso, true)}`;
    } else {
      const sameYear = startD.getFullYear() === endD.getFullYear();
      return `⏰ dal ${formatDateOnly(startIso, !sameYear)} al ${formatDateOnly(endIso, true)}`;
    }
  }

  // Case 3: Mixed (one reliable, one placeholder)
  if (startIso && endIso) {
    const startStr = hasReliableStart
      ? formatDateTime(startIso, false)
      : formatDateOnly(startIso, false);
    const endStr = hasReliableEnd ? formatDateTime(endIso, true) : formatDateOnly(endIso, true);
    return `⏰ ${startStr} → ${endStr}`;
  }

  // Case 4: Only start is specified
  if (startIso) {
    return hasReliableStart
      ? `⏰ dalle ${formatDateTime(startIso, true)}`
      : `⏰ dal ${formatDateOnly(startIso, true)}`;
  }

  // Case 5: Only end is specified
  if (endIso) {
    return hasReliableEnd
      ? `⏰ fino alle ${formatDateTime(endIso, true)}`
      : `⏰ fino al ${formatDateOnly(endIso, true)}`;
  }

  return "";
}
